import React, { useState, useEffect } from 'react';
import { Flag, Search, Filter, Trash2, AlertTriangle, Eye, X, BarChart3 } from 'lucide-react';
import PostManagementModal from '../../components/admin/PostManagementModal';
import api from '../../services/api';

interface Report {
  _id: string;
  reason: string;
  description: string;
  sport: string;
  status: string;
  priority: string;
  createdAt: string;
  reporter: {
    _id: string;
    name: string;
    email: string;
    avatar?: string;
  } | null;
  reportedPost: {
    _id: string;
    content: string;
    images?: Array<{ url: string }>;
    author: {
      _id: string;
      name: string;
      avatar?: string;
    } | null;
  } | null;
}

interface Stats {
  pending: number;
  resolved: number;
}

const ModerationPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'reports' | 'logs'>('reports');
  const [reports, setReports] = useState<Report[]>([]);
  const [stats, setStats] = useState<Stats>({ pending: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [reasonFilter, setReasonFilter] = useState('all');
  const [sportFilter, setSportFilter] = useState('all');
  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [showPostModal, setShowPostModal] = useState<{
    isOpen: boolean;
    reportId: string;
    report: Report | null;
  }>({
    isOpen: false,
    reportId: '',
    report: null
  });

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [reasonFilter, sportFilter]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const resp = await api.get('/admin/moderation/reports', {
        params: {
          ...(reasonFilter !== 'all' ? { reason: reasonFilter } : {}),
          ...(sportFilter !== 'all' ? { sport: sportFilter } : {}),
          ...(searchQuery ? { search: searchQuery } : {}),
        },
      });
      if (resp.data?.success) {
        setReports(resp.data.data.reports || []);
      } else {
        throw new Error(resp.data?.error?.message || 'Failed to fetch reports');
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const resp = await api.get('/admin/moderation/stats');
      if (resp.data?.success) {
        setStats({
          pending: resp.data.data.summary.pending || 0,
          resolved: resp.data.data.summary.resolved || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({ pending: 0, resolved: 0 });
    }
  };

  const filteredReports = reports.filter(report => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    
    const contentMatch = report.reportedPost?.content?.toLowerCase().includes(query) || false;
    const authorMatch = report.reportedPost?.author?.name?.toLowerCase().includes(query) || false;
    const reporterMatch = report.reporter?.name?.toLowerCase().includes(query) || false;
    const descriptionMatch = report.description?.toLowerCase().includes(query) || false;
    
    return contentMatch || authorMatch || reporterMatch || descriptionMatch;
  });

  const getReasonBadgeColor = (reason: string) => {
    const colors: Record<string, string> = {
      inappropriate: 'bg-orange-100 text-orange-800',
      spam: 'bg-red-100 text-red-800',
      abuse: 'bg-red-100 text-red-800',
      harassment: 'bg-purple-100 text-purple-800',
      hate_speech: 'bg-red-100 text-red-800',
      bullying: 'bg-orange-100 text-orange-800',
      false_information: 'bg-yellow-100 text-yellow-800',
      copyright: 'bg-blue-100 text-blue-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[reason] || 'bg-gray-100 text-gray-800';
  };

  // Action Handlers
  const handleDeletePost = async (reportId: string) => {
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    try {
      const resp = await api.post(`/admin/moderation/reports/${reportId}/delete-post`, {
        reason: 'Policy violation',
        notifyUser: true,
      });
      if (resp.data?.success) {
        alert('Post deleted successfully');
        fetchReports();
        fetchStats();
      } else {
        alert('Failed to delete post: ' + (resp.data?.error?.message || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post: ' + (error?.response?.data?.error?.message || 'Please try again.'));
    }
  };

  const handleWarnUser = async (reportId: string) => {
    const message = prompt('Enter warning message for the user:');
    if (!message) return;

    try {
      const resp = await api.post(`/admin/moderation/reports/${reportId}/warn-user`, {
        customMessage: message,
        severity: 'moderate',
        addStrike: true,
      });
      if (resp.data?.success) {
        alert('Warning sent successfully');
        fetchReports();
        fetchStats();
      } else {
        alert('Failed to send warning: ' + (resp.data?.error?.message || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error warning user:', error);
      alert('Failed to send warning: ' + (error?.response?.data?.error?.message || 'Please try again.'));
    }
  };

  const handleIgnoreReport = async (reportId: string) => {
    const notes = prompt('Enter notes for ignoring this report (optional):') || '';

    try {
      const resp = await api.post(`/admin/moderation/reports/${reportId}/ignore`, { notes });
      if (resp.data?.success) {
        alert('Report ignored successfully');
        fetchReports();
        fetchStats();
      } else {
        alert('Failed to ignore report: ' + (resp.data?.error?.message || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error ignoring report:', error);
      alert('Failed to ignore report: ' + (error?.response?.data?.error?.message || 'Please try again.'));
    }
  };

  const handleViewFullPost = (report: Report) => {
    setShowPostModal({
      isOpen: true,
      reportId: report._id,
      report: report
    });
  };

  const closePostModal = () => {
    setShowPostModal({
      isOpen: false,
      reportId: '',
      report: null
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Moderation</h1>
          <div className="flex gap-3">
            <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
              {stats.pending} Pending
            </span>
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              {stats.resolved} Resolved
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex gap-8">
          <button
            onClick={() => setActiveTab('reports')}
            className={`py-4 px-2 border-b-2 font-medium text-sm ${
              activeTab === 'reports'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Reported Posts
            {stats.pending > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs">
                {stats.pending}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`py-4 px-2 border-b-2 font-medium text-sm ${
              activeTab === 'logs'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Moderation Logs
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'reports' && (
          <>
            {/* Search and Filters */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search posts, authors, or reporters..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={reasonFilter}
                  onChange={(e) => setReasonFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Reasons</option>
                  <option value="inappropriate">Inappropriate</option>
                  <option value="spam">Spam</option>
                  <option value="abuse">Abuse</option>
                  <option value="harassment">Harassment</option>
                  <option value="hate_speech">Hate Speech</option>
                  <option value="bullying">Bullying</option>
                  <option value="false_information">False Information</option>
                  <option value="copyright">Copyright</option>
                  <option value="other">Other</option>
                </select>
                <select
                  value={sportFilter}
                  onChange={(e) => setSportFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Sports</option>
                  <option value="chess">Chess</option>
                  <option value="tennis">Tennis</option>
                  <option value="football">Football</option>
                  <option value="cricket">Cricket</option>
                  <option value="basketball">Basketball</option>
                  <option value="swimming">Swimming</option>
                </select>
              </div>
            </div>

            {/* Reports List */}
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading reports...</p>
                </div>
              ) : filteredReports.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                  <Flag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No reported posts</h3>
                  <p className="text-gray-600">There are no reports matching your filters at this time.</p>
                </div>
              ) : (
                filteredReports.map((report) => (
                  <div key={report._id} className="bg-white rounded-lg shadow-sm p-6 border-2 border-gray-200">
                    <div className="flex items-start gap-4">
                      <Flag className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                      
                      <div className="flex-1">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getReasonBadgeColor(report.reason)}`}>
                              {report.reason.replace('_', ' ').toUpperCase()}
                            </span>
                            {report.sport && (
                              <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                                {report.sport.toUpperCase()}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Reporter Info */}
                        <p className="text-sm text-gray-600 mb-3">
                          Reported by <span className="font-medium">{report.reporter?.name || 'Unknown User'}</span> •{' '}
                          {new Date(report.createdAt).toLocaleDateString()} at{' '}
                          {new Date(report.createdAt).toLocaleTimeString()}
                        </p>

                        {/* Post Content */}
                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-white font-medium flex-shrink-0">
                              {report.reportedPost?.author?.name?.charAt(0) || '?'}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 mb-1">
                                {report.reportedPost?.author?.name || 'Unknown User'}
                              </p>
                              <p className="text-gray-700">{report.reportedPost?.content}</p>
                              {report.reportedPost?.images && report.reportedPost.images.length > 0 && (
                                <div className="mt-3 grid grid-cols-2 gap-2">
                                  {report.reportedPost.images.map((img, idx) => (
                                    <img
                                      key={idx}
                                      src={img.url}
                                      alt="Post attachment"
                                      className="rounded-lg w-full h-32 object-cover"
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3">
                          <button 
                            onClick={() => handleDeletePost(report._id)}
                            className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Post
                          </button>
                          <button 
                            onClick={() => handleWarnUser(report._id)}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <AlertTriangle className="w-4 h-4" />
                            Warn User
                          </button>
                          <button 
                            onClick={() => handleIgnoreReport(report._id)}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <X className="w-4 h-4" />
                            Ignore Report
                          </button>
                          <button 
                            onClick={() => handleViewFullPost(report)}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            View Full Post
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {activeTab === 'logs' && (
          <ModerationLogsTab />
        )}
      </div>

      {/* Post Management Modal */}
      <PostManagementModal
        isOpen={showPostModal.isOpen}
        onClose={closePostModal}
        reportId={showPostModal.reportId}
        report={showPostModal.report}
        onRefresh={() => {
          fetchReports();
          fetchStats();
        }}
      />
    </div>
  );
};

// Moderation Logs Tab Component
const ModerationLogsTab: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, [actionFilter]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const resp = await api.get('/admin/moderation/logs', {
        params: {
          ...(actionFilter !== 'all' ? { action: actionFilter } : {}),
        },
      });
      if (resp.data?.success) {
        setLogs(resp.data.data.logs || []);
      } else {
        console.error('Failed to fetch logs:', resp.data?.error);
        setLogs([]);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getActionBadgeColor = (action: string) => {
    const colors: Record<string, string> = {
      deleted: 'bg-red-100 text-red-800',
      warned: 'bg-yellow-100 text-yellow-800',
      ignored: 'bg-gray-100 text-gray-800',
      banned: 'bg-red-100 text-red-800',
      edited: 'bg-blue-100 text-blue-800'
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'deleted':
        return <Trash2 className="w-4 h-4" />;
      case 'warned':
        return <AlertTriangle className="w-4 h-4" />;
      case 'ignored':
        return <X className="w-4 h-4" />;
      default:
        return <Flag className="w-4 h-4" />;
    }
  };

  return (
    <div>
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex gap-4">
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Actions</option>
            <option value="deleted">Deleted</option>
            <option value="warned">Warned</option>
            <option value="ignored">Ignored</option>
            <option value="banned">Banned</option>
            <option value="edited">Edited</option>
          </select>
        </div>
      </div>

      {/* Logs List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading logs...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No moderation logs</h3>
          <p className="text-gray-600">There are no moderation actions recorded yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Moderator
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Content
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((log) => (
                <tr key={log._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getActionBadgeColor(log.action)}`}>
                        {getActionIcon(log.action)}
                        {log.action.toUpperCase()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {log.moderator?.name || 'Unknown'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {log.moderator?.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {log.targetUser?.name || 'Unknown'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {log.targetUser?.email}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">
                      {log.targetContent?.contentPreview || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {log.targetContent?.contentType}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(log.timestamp).toLocaleDateString()}
                    <br />
                    {new Date(log.timestamp).toLocaleTimeString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ModerationPage;